import { useState } from "react";
import css from "../Workflows.module.css";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { convertDate } from "../../../../../services/luxonHelper";
import CheckboxComponent from "@CubeComponents/form/CheckboxComponent";
import DetailsIcon from "@Components/StickySidebarViewer/assets/Details";
import UserNameTagComponent from "../../../../../cubeComponent/html/UserNameTagComponent";
import { useSelector } from "react-redux";
import { getSingleTransmittalPageUrl } from "../../../../../services/utils/helpers";
import { useNavigate } from "react-router-dom";
import IconComponent from "@CubeComponents/icons";

function ReviewLevelTree({
  reviewLevels,
  showCheckboxes = false,
  showProgress = false,
  showNotifyBtn = false,
  changeLevelCheckboxStatusHandler,
  changeReviewerCheckboxStatusHandler,
  notifyUserInReviewCallback,
}) {
  const navigate = useNavigate();
  const initialState = {};
  reviewLevels.map(({ serial }) => (initialState[serial] = true));

  const [reviewLevelsAccState, setReviewLevelsAccState] = useState(initialState);
  const [isHovered, setIsHovered] = useState({});
  const projectData = useSelector((state) => state.projectData);
  const notifyTimeOut = 86400000;

  const levelTimelineCircleMapper = {
    approved: "checkMark",
    revoked: "revokeMark",
    in_progress: "inProgressMark",
    pending: "defaultMark",
    bypassed: "bypassedMark",
  };

  const reviewerTimelineCircleMapper = {
    in_progress: {
      approve: "checkMark",
      revoke: "revokeMark",
      skip: "skippedMark",
      auto_skipped: "skippedMark",
    },
    approved: {
      approve: "checkMark",
      revoke: "revokeMark",
      skip: "skippedMark",
      auto_skipped: "skippedMark",
    },
    pending: {
      approve: "checkMark",
      revoke: "revokeMark",
      skip: "skippedMark",
      auto_skipped: "skippedMark",
    },
    revoked: {
      approve: "checkMark",
      revoke: "revokeMark",
      skip: "skippedMark",
      auto_skipped: "skippedMark",
    },
    bypassed: {
      approve: "checkMark",
      revoke: "revokeMark",
      skip: "skippedMark",
      auto_skipped: "skippedMark",
    },
  };

  const getNestedChildDiv = (childClassName) => <div className={`${css.nestedChildDiv} ${css[childClassName]}`} />;

  const getInProgressChildDiv = (angle) => {
    let angleRect1 = 90;
    let angleRect2 = 90;
    let angleRect3 = 90;
    let angleRect4 = 90;

    if (angle > 0) {
      angleRect1 = 90 - angle > 0 ? 90 - angle : 0;
    }

    if (angle > 90) {
      angleRect2 = 180 - angle > 0 ? 180 - angle : 0;
    }

    if (angle > 180) {
      angleRect3 = 270 - angle > 0 ? 270 - angle : 0;
    }

    if (angle > 270) {
      angleRect4 = 360 - angle > 0 ? 360 - angle : 0;
    }

    const styleRect1 = { transform: `skew(0, -${angleRect1}deg)` };
    const styleRect2 = { transform: `skew(${angleRect2}deg)` };
    const styleRect3 = { transform: `skew(0, -${angleRect3}deg)` };
    const styleRect4 = { transform: `skew(${angleRect4}deg)` };

    return (
      <div className={css.inProgressMarkBaseCircle}>
        <div className={css.inProgressMarkCover} />
        <div className={css.inProgressMarkRect1} style={styleRect1} />
        <div className={css.inProgressMarkRect2} style={styleRect2} />
        <div className={css.inProgressMarkRect3} style={styleRect3} />
        <div className={css.inProgressMarkRect4} style={styleRect4} />
      </div>
    );
  };

  const getCrossIconChildDiv = () => (
    <div className={css.crosssign}>
      <div className={css.crosssign_circle} />
      <div className={css.crosssign_stem} />
      <div className={css.crosssign_stem2} />
    </div>
  );

  const timelineCircleStyleMapper = {
    checkMark: {
      childClassName: "checkMarkTick",
      className: "checkMark",
      content: "Submitted",
      getNestedChild: getNestedChildDiv,
    },
    inProgressMark: {
      className: "inProgressMark",
      content: "In Progress",
      getNestedChild: getInProgressChildDiv,
    },
    skippedMark: {
      className: "skippedMark",
      content: "Skipped",
      getNestedChild: getNestedChildDiv,
    },
    revokeMark: {
      className: "revokeMark",
      content: "Revoked",
      getNestedChild: getCrossIconChildDiv,
    },
    waitingMark: {
      className: "waitingMark",
      content: "Waiting",
      getNestedChild: getNestedChildDiv,
    },
    defaultMark: {
      className: "defaultMark",
      content: "",
      getNestedChild: getNestedChildDiv,
    },
    bypassedMark: {
      className: "bypassedMark",
      content: "Bypassed",
      getNestedChild: getNestedChildDiv,
    },
  };

  const userResponseRanking = {
    skip: 1,
    auto_skipped: 2,
    approve: 3,
    revoke: 4,
  };

  const changeAccStateHandler = (serial) => {
    setReviewLevelsAccState((prev) => ({ ...prev, [serial]: !prev[serial] }));
  };

  const calculateLevelTimelineCircleType = (clearanceStatus) => {
    const type = showProgress ? levelTimelineCircleMapper[clearanceStatus] : null;
    return type;
  };

  const calculateReviewerTimelineCircleType = (clearanceStatus, response) => {
    let type;

    if (response) {
      type = reviewerTimelineCircleMapper[clearanceStatus]?.[response];
    } else if (["in_progress"].includes(clearanceStatus)) {
      type = "waitingMark";
    } else if (["pending", "revoked"].includes(clearanceStatus)) {
      type = "defaultMark";
    } else if (["bypassed"].includes(clearanceStatus)) {
      type = "bypassedMark";
    }

    return showProgress ? type : null;
  };

  const getUserAvatarComponent = (user) => <UserNameTagComponent id={user.userId} />;

  const getReviewerCheckbox = (_id, userId, isReviewerSelected) => (
    <div className={css.checkboxSymbol}>
      <CheckboxComponent
        checked={isReviewerSelected}
        change={(e) => changeReviewerCheckboxStatusHandler(e, _id, userId)}
      />
    </div>
  );

  const getLevelCheckbox = (_id, levelSelectionState) => {
    const isChecked = levelSelectionState === "complete";
    const isPartial = levelSelectionState === "partial";

    if (isPartial) {
      return (
        <div className={css.checkboxSymbol}>
          <CheckboxComponent indeterminate={isPartial} change={(e) => changeLevelCheckboxStatusHandler(e, _id)} />
        </div>
      );
    }

    return (
      <div className={css.checkboxSymbol}>
        <CheckboxComponent checked={isChecked} change={(e) => changeLevelCheckboxStatusHandler(e, _id)} />
      </div>
    );
  };

  const getTimelineCircle = (type, levelIndex, reviewerIndex, progressAngle) => {
    const zIndex = parseInt(`${levelIndex}${reviewerIndex}`);
    const style = { "--zindexprogressline": zIndex };

    type = type || "defaultMark";
    const { className, content, childClassName, getNestedChild } = timelineCircleStyleMapper[type];

    const args = type === "inProgressMark" ? progressAngle : childClassName;

    if (content) {
      return (
        <>
          <div className={`${css.timelineSymbol} ${css[className]}`} style={style}>
            <ToolTipComponent showTipPointer position="left" content={content}>
              <div className={css.nestedChildWrapper}>{getNestedChild(args)}</div>
            </ToolTipComponent>
          </div>
        </>
      );
    }

    return (
      <>
        <div className={`${css.timelineSymbol} ${css[className]}`} style={style}>
          <div className={css.nestedChildWrapper}>{getNestedChild(args)}</div>
        </div>
      </>
    );
  };

  const header = (_id, serial, title, type, clearanceStatus, levelSelectionState, progressAngle) => (
    <div className={css.accHeader}>
      {!showCheckboxes ? getTimelineCircle(type, serial, 0, progressAngle) : getLevelCheckbox(_id, levelSelectionState)}
      <span
        className={
          clearanceStatus === "pending" && showProgress ? css.reviewLevelAccSerialDisabled : css.reviewLevelAccSerial
        }
      >
        {`Level ${serial}`}
      </span>
      <span className={clearanceStatus === "pending" && showProgress ? "opacity-30" : undefined}>{title}</span>
    </div>
  );

  const compareUserResponsesMapper = (a, b) => {
    const priorityA = a.response ? userResponseRanking[a.response] : 5;
    const priorityB = b.response ? userResponseRanking[b.response] : 5;

    return priorityA - priorityB;
  };

  const getTooltipContent = (clearanceStatus, response, lastNotified, currentTimestamp) => {
    if (clearanceStatus !== "in_progress") {
      return "The user belongs to a level which is not in progress";
    }

    if (response) {
      return "The user has already submitted their response";
    }

    if (lastNotified && currentTimestamp - lastNotified < notifyTimeOut) {
      return "You can notify this user again after 24 hours";
    }
  };

  const handleOpenTransmittalLink = (lastTransmittal) => () => {
    const { transmittalId } = lastTransmittal;

    const singleTransmittalPageURL = getSingleTransmittalPageUrl(projectData._id, transmittalId);

    if (singleTransmittalPageURL) {
      navigate(singleTransmittalPageURL);
    }
  };

  const content = (_id, reviewers, clearanceStatus, serial) => {
    const sortedReviewers = [...reviewers];
    if (showProgress) {
      sortedReviewers.sort(compareUserResponsesMapper);
    }

    const currentTimestamp = Date.now();
    return (
      <div className={css.accContent}>
        {sortedReviewers.map((reviewer, index) => {
          const {
            userId,
            response,
            isReviewerSelected,
            responseTimestamp,
            isUserDeleted,
            comment,
            lastNotified,
            attachedToTransmittal,
            firstName,
            lastName,
          } = reviewer;
          const type = calculateReviewerTimelineCircleType(clearanceStatus, response);

          const lastTransmittal = attachedToTransmittal?.at(-1);

          const enableNotifyBtn =
            (!lastNotified || currentTimestamp - lastNotified >= notifyTimeOut) &&
            clearanceStatus === "in_progress" &&
            !response;
          const tooltipText = enableNotifyBtn
            ? "Notify User"
            : getTooltipContent(clearanceStatus, response, lastNotified, currentTimestamp);

          return (
            <div
              key={index}
              className={css.reviewerContainer}
              onMouseEnter={() =>
                setIsHovered((prev) => ({
                  ...prev,
                  [`${_id}-${userId}`]: true,
                }))
              }
              onMouseLeave={() =>
                setIsHovered((prev) => ({
                  ...prev,
                  [`${_id}-${userId}`]: false,
                }))
              }
            >
              <div className={css.reviewerSubContainer}>
                <div className={css.reviewerWithTimelineCircle}>
                  {!showCheckboxes
                    ? getTimelineCircle(type, serial, index + 1)
                    : getReviewerCheckbox(_id, userId, isReviewerSelected)}
                  <div
                    className={
                      clearanceStatus === "pending" && showProgress ? css.userWrapperDisabled : css.userWrapper
                    }
                  >
                    <span
                      className="text-[var(--size-16)] font-semibold flex-1 overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                      data-testid="userProfileDetail"
                    >
                      {getUserAvatarComponent(reviewer)}
                    </span>

                    {isUserDeleted && (
                      <ToolTipComponent
                        showTipPointer
                        position="bottom"
                        content="The reviewer has been removed from this space"
                      >
                        <DetailsIcon size="24" color="#FF0000" />
                      </ToolTipComponent>
                    )}
                    {lastTransmittal ? (
                      <IconComponent
                        position="bottom"
                        toolTipContent={`Transmittal TS-${
                          lastTransmittal.transmittalIndexAtProjectLevel
                        } sent by ${firstName} ${lastName ? `${lastName} ` : ""}on ${convertDate(
                          Number(lastTransmittal.createdDate),
                          "dateForText",
                        )}`}
                        className="cursor-pointer"
                        name="TransmittalIcon"
                        onClick={handleOpenTransmittalLink(lastTransmittal)}
                      />
                    ) : undefined}
                    {showNotifyBtn && isHovered[`${_id}-${userId}`] ? (
                      <IconComponent
                        position="bottom"
                        toolTipContent={tooltipText}
                        isEnabled={enableNotifyBtn}
                        onClick={() => notifyUserInReviewCallback(_id, userId)}
                        name="BellIcon"
                      />
                    ) : undefined}
                  </div>
                </div>
                {responseTimestamp && (
                  <span className={css.responseTimestamp}>{convertDate(Number(responseTimestamp), "dateForText")}</span>
                )}
              </div>
              {comment && ["approve", "revoke", "skip"].includes(response) && (
                <p className={css.reviewerComment}>{comment}</p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={css.reviewLevelsTreeContainer}>
      {reviewLevels.map((reviewLevel, index) => {
        const { _id, title, serial, reviewers, clearanceStatus, levelSelectionState } = reviewLevel;
        const type = calculateLevelTimelineCircleType(clearanceStatus);
        const className = css.accordionMUICustomStyle;
        let angle = 360;

        if (showProgress) {
          const totalReviewers = reviewers.length;
          const reviewersWithResponse = reviewers.filter((reviewer) => reviewer.response).length;

          const ratio = reviewersWithResponse / totalReviewers;
          angle = ratio * 360;
        }

        return (
          <Accordion
            key={index}
            disableGutters
            expanded={reviewLevelsAccState[serial]}
            onChange={() => changeAccStateHandler(serial)}
            className={className}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className={css.accordionHeaderMUICustomStyle}>
              {header(_id, serial, title, type, clearanceStatus, levelSelectionState, angle)}
            </AccordionSummary>
            <AccordionDetails className={css.accordionContentMUICustomStyle}>
              {content(_id, reviewers, clearanceStatus, serial)}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </div>
  );
}

export default ReviewLevelTree;
